import { useMemo, useState } from "react";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { FiAlertOctagon, FiHelpCircle } from "react-icons/fi";
import { Modal, Button } from "flowbite-react";
import { formatToReal } from "../../../utils/utils";

const StatusBlock = ({
  title,
  hasRestrictions,
  total = 0,
  messageError,
  messageSuccess,
  description, // opcional: subtítulo no modal
  details, // opcional: corpo do modal (string ou JSX)
}) => {
  const [open, setOpen] = useState(false);
  const isError = !!hasRestrictions;

  // Paleta sóbria + compacta
  const ui = useMemo(
    () =>
      isError
        ? {
            badgeDot: "bg-red-500",
            iconWrap: "bg-red-100 text-red-700 border-red-200",
            statusText: "text-red-700",
            border: "border-red-200",
            headerBar: "bg-red-50 border-red-100",
          }
        : {
            badgeDot: "bg-blue-600",
            iconWrap: "bg-blue-100 text-blue-700 border-blue-200",
            statusText: "text-blue-800",
            border: "border-blue-200",
            headerBar: "bg-blue-50 border-blue-100",
          },
    [isError]
  );

  const icon = isError ? (
    <FiAlertOctagon className="w-4 h-4" />
  ) : (
    <IoMdCheckmarkCircleOutline className="w-4 h-4" />
  );

  let message = isError ? messageError : messageSuccess || "Nada consta";

  message = isError && total > 0 ? `${message} / ${formatToReal(total)}` : message;

  return (
    <>
      {/* CARD COMPACTO */}
      <div
        className={[
          "rounded-lg border bg-gradient-to-b from-white to-gray-50",
          "hover:bg-white transition-colors",
          "shadow-sm hover:shadow",
          "min-h-[56px]",
          ui.border,
        ].join(" ")}
      >
        <div className="flex items-center justify-between px-3 py-2.5">
          {/* Esquerda: medalhão + título + status */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className={[
                "h-8 w-8 rounded-full border",
                "inline-flex items-center justify-center",
                ui.iconWrap,
              ].join(" ")}
              aria-hidden="true"
            >
              {icon}
            </div>

            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-0.5">
                <h3 className="text-[13px] font-semibold text-gray-900 truncate">
                  {title}
                </h3>

                {/* “?” compacto para abrir modal */}
                {details && (
                  <button
                    type="button"
                    aria-label={`Mais detalhes sobre ${title}`}
                    onClick={() => setOpen(true)}
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-500 transition"
                  >
                    <FiHelpCircle className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <p
                className={[
                  "text-[10px] font-medium truncate",
                  ui.statusText,
                ].join(" ")}
                title={typeof message === "string" ? message : undefined}
              >
                {message}
              </p>
            </div>
          </div>

          {/* Badge de estado, discreta */}
          <span className="hidden sm:inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${ui.badgeDot}`} />
            {isError ? "Atenção" : "OK"}
          </span>
        </div>
      </div>

      {/* MODAL COMPACTO & BONITO */}
      {details && (
        <Modal show={open} size="xl" onClose={() => setOpen(false)} popup>
          <Modal.Header className={`!p-0 !border-b ${ui.headerBar}`}>
            <div className="w-full px-4 py-3.5">
              <div className="flex items-center gap-2.5">
                <div
                  className={[
                    "h-9 w-9 rounded-full border",
                    "inline-flex items-center justify-center",
                    ui.iconWrap,
                  ].join(" ")}
                >
                  {isError ? (
                    <FiAlertOctagon className="w-4 h-4" />
                  ) : (
                    <IoMdCheckmarkCircleOutline className="w-4 h-4" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[15px] font-semibold text-gray-900 truncate">
                      {title}
                    </h4>
                    <span className="inline-flex items-center gap-1.5 rounded-full border px-2 text-xs text-[10.5px] font-medium text-gray-700 border-gray-200 bg-white">
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${ui.badgeDot}`}
                      />
                      {isError ? "Ocorrências" : "Nada consta"}
                      {isError && total > 0 && ` / ${formatToReal(total)}`}
                    </span>
                  </div>
                  {description && (
                    <p className="-mt-1 text-[12px] text-gray-600 truncate">
                      {description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="space-y-4">
              {/* Resumo rápido */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="mt-4">
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-[13.5px] font-semibold text-gray-900">
                    {message}
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="border-t border-gray-100 pt-3">
                {typeof details === "string" ? (
                  <p className="text-[13.5px] leading-relaxed text-gray-800 whitespace-pre-line">
                    {details}
                  </p>
                ) : (
                  details
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-end">
              <Button
                color="gray"
                onClick={() => setOpen(false)}
                className="shadow-none !px-3 !py-1.5 text-sm"
              >
                Fechar
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default StatusBlock;
